import {
  Component,
  signal,
  inject,
  HostListener,
  computed,
  effect,
  untracked,
} from '@angular/core';
import { ActivatedRoute, Router, ParamMap } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  CategoryFilters,
  CategoryFiltersChangeEvent,
} from './category-filters/category-filters';
import { ProductCard } from '@/app/elements/product-card/product-card';
import {
  ContentApi,
  SiteCategoryBrowseResponse,
  SiteProductCardDto,
  SiteCategoryProductSearchRequest,
  SiteCategoryBrowseSubCategory,
} from '@/app/services/content-api';
import { combineLatest, firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from 'sham-components/services';
import { mapProductCard } from '@/app/utils/product-card.mapper';

@Component({
  selector: 'app-category-details',
  templateUrl: './category-details.html',
  styleUrl: './category-details.css',
  imports: [FormsModule, CategoryFilters, ProductCard, TranslateModule],
})
export class CategoryDetails {
  private readonly api = inject(ContentApi);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);

  browseResponse = signal<SiteCategoryBrowseResponse | null>(null);

  products = signal<SiteProductCardDto[]>([]);
  page = signal(1);
  pageSize = signal(20);
  totalCount = signal(0);
  isLoading = signal(false);

  currentFilters = signal<CategoryFiltersChangeEvent>({
    subCategories: [],
    brands: [],
    minPrice: 0,
    maxPrice: 0,
    ratings: [],
    freeDelivery: false,
    newItems: false,
  });

  sortBy = signal('latest');
  searchTerm = signal('');
  activeSubSlug = signal<string | null>(null);
  activeSub2Slug = signal<string | null>(null);
  currentLang = computed(() => (this.languageService.currentLang() === 'ar' ? 'ar' : 'en'));
  filterQueryParams = computed(() => this.buildFilterQueryParams());
  private lastRouteKey: string | null = null;

  constructor() {
    effect(() => {
      const lang = this.currentLang();
      const routeKey = this.lastRouteKey;

      if (!routeKey) {
        return;
      }

      untracked(() => {
        void this.reloadCurrentCategoryForLanguage(lang);
      });
    });

    combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(
      ([params, queryParams]) => {
        const slugValue = params.get('slug');
        const subSlug = params.get('sub');
        const sub2Slug = params.get('sub2');

        this.activeSubSlug.set(subSlug);
        this.activeSub2Slug.set(sub2Slug);

        if (slugValue) {
          const routeKey = `${slugValue}|${subSlug || ''}|${sub2Slug || ''}`;
          if (this.lastRouteKey !== routeKey) {
            this.lastRouteKey = routeKey;
            void this.loadCategoryFromRoute(
              slugValue,
              subSlug,
              sub2Slug,
              queryParams,
            );
          } else {
            void this.applyQueryState(queryParams, subSlug, sub2Slug);
          }
        }
      },
    );
  }

  private async loadCategoryFromRoute(
    slug: string,
    subSlug: string | null,
    sub2Slug: string | null,
    queryParams: ParamMap,
  ) {
    await this.ensureDefaultQueryParams(queryParams);

    const routeFilters = this.readNonSubFiltersFromQuery(queryParams);
    const routeSort = queryParams.get('sort') || 'latest';
    this.sortBy.set(routeSort);
    this.searchTerm.set(routeFilters.searchTerm);

    await this.loadInitialData(slug);

    this.currentFilters.update((f) => ({
      ...f,
      brands: routeFilters.brands,
      ratings: routeFilters.ratings,
      freeDelivery: routeFilters.freeDelivery,
      newItems: routeFilters.newItems,
      minPrice:
        routeFilters.minPrice ??
        this.browseResponse()?.priceRange.minPrice ??
        f.minPrice,
      maxPrice:
        routeFilters.maxPrice ??
        this.browseResponse()?.priceRange.maxPrice ??
        f.maxPrice,
    }));

    this.applySubCategoryFilterFromRoute(subSlug, sub2Slug);

    const shouldApplySearch = !!subSlug || this.hasActiveNonSubCategoryFilters();
    if (shouldApplySearch) {
      await this.applyFiltersAndSearch(true);
    }
  }

  private async applyQueryState(
    queryParams: ParamMap,
    subSlug: string | null,
    sub2Slug: string | null,
  ) {
    await this.ensureDefaultQueryParams(queryParams);

    const routeFilters = this.readNonSubFiltersFromQuery(queryParams);
    const routeSort = queryParams.get('sort') || 'latest';
    this.sortBy.set(routeSort);
    this.searchTerm.set(routeFilters.searchTerm);

    this.currentFilters.update((f) => ({
      ...f,
      brands: routeFilters.brands,
      ratings: routeFilters.ratings,
      freeDelivery: routeFilters.freeDelivery,
      newItems: routeFilters.newItems,
      minPrice:
        routeFilters.minPrice ??
        this.browseResponse()?.priceRange.minPrice ??
        f.minPrice,
      maxPrice:
        routeFilters.maxPrice ??
        this.browseResponse()?.priceRange.maxPrice ??
        f.maxPrice,
    }));
    this.applySubCategoryFilterFromRoute(subSlug, sub2Slug);

    if (this.hasActiveNonSubCategoryFilters() || !!subSlug) {
      await this.applyFiltersAndSearch(true);
    }
  }

  async loadInitialData(slug: string) {
    this.isLoading.set(true);
    try {
      const response = await firstValueFrom(
        this.api.getCategoryBrowse(
          slug,
          this.currentLang(),
          this.sortBy(),
          1,
          this.pageSize(),
        ),
      );
      if (response) {
        this.browseResponse.set(response);
        this.products.set(response.products.items);
        this.page.set(response.products.page);
        this.totalCount.set(response.products.totalCount);
        this.currentFilters.update((f) => ({
          ...f,
          minPrice: response.priceRange.minPrice,
          maxPrice: response.priceRange.maxPrice,
        }));
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  async onFiltersChanged(event: CategoryFiltersChangeEvent) {
    this.currentFilters.set(event);
    void this.syncQueryParams();
    await this.applyFiltersAndSearch(true);
  }

  async onSortChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.sortBy.set(value);
    void this.syncQueryParams();
    await this.applyFiltersAndSearch(true);
  }

  async applyFiltersAndSearch(resetPage: boolean = true) {
    if (resetPage) this.page.set(1);

    const filters = this.currentFilters();
    const req: SiteCategoryProductSearchRequest = {
      categoryId: filters.subCategories?.length
        ? filters.subCategories[0]
        : this.browseResponse()?.categoryId || '',
      brandIds: filters.brands.length ? filters.brands : undefined,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      ratings: filters.ratings.length ? filters.ratings : undefined,
      searchTerm: this.searchTerm() || undefined,
      freeDelivery: filters.freeDelivery,
      newItems: filters.newItems,
      sortBy: this.sortBy(),
      page: this.page(),
      pageSize: this.pageSize(),
    };

    if (!req.categoryId) return;

    this.isLoading.set(true);
    try {
      const response = await firstValueFrom(this.api.searchProducts(req));
      if (response) {
        if (resetPage) {
          this.products.set(response.items);
        } else {
          this.products.update((prev) => [...prev, ...response.items]);
        }
        this.totalCount.set(response.totalCount);
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  async loadMore() {
    if (this.isLoading() || this.products().length >= this.totalCount()) return;
    this.page.update((p) => p + 1);
    await this.applyFiltersAndSearch(false);
  }

  mapProduct(dto: SiteProductCardDto) {
    return mapProductCard(dto);
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const threshold = 150;
    const position = window.scrollY + window.innerHeight;
    const height = document.documentElement.scrollHeight;

    if (position >= height - threshold) {
      if (!this.isLoading() && this.products().length < this.totalCount()) {
        void this.loadMore();
      }
    }
  }

  private applySubCategoryFilterFromRoute(
    subSlug: string | null,
    sub2Slug: string | null,
  ) {
    const selectedSubCategoryIds = this.resolveSubCategoryIdsBySlugs(
      this.browseResponse()?.subCategories || [],
      subSlug,
      sub2Slug,
    );

    this.currentFilters.update((f) => ({
      ...f,
      subCategories: selectedSubCategoryIds,
    }));
  }

  private resolveSubCategoryIdsBySlugs(
    subCategories: SiteCategoryBrowseSubCategory[],
    subSlug: string | null,
    sub2Slug: string | null,
  ): string[] {
    if (!subSlug) return [];

    const parent = subCategories.find((item) => item.slug === subSlug);
    if (!parent) return [];

    if (!sub2Slug) return [parent.id];

    const child = parent.children.find((item) => item.slug === sub2Slug);
    return child ? [child.id] : [parent.id];
  }

  private hasActiveNonSubCategoryFilters(): boolean {
    const filters = this.currentFilters();
    const range = this.browseResponse()?.priceRange;
    const minPriceChanged = range ? filters.minPrice !== range.minPrice : false;
    const maxPriceChanged = range ? filters.maxPrice !== range.maxPrice : false;

    return (
      filters.brands.length > 0 ||
      filters.ratings.length > 0 ||
      filters.freeDelivery ||
      filters.newItems ||
      !!this.searchTerm() ||
      minPriceChanged ||
      maxPriceChanged
    );
  }

  private readNonSubFiltersFromQuery(queryParams: ParamMap) {
    const brands = this.parseStringListQueryParam(queryParams.get('brands'));
    const ratings = this.parseNumberListQueryParam(queryParams.get('ratings'));
    const freeDelivery = queryParams.get('freeDelivery') === '1';
    const newItems = queryParams.get('newItems') === '1';
    const minPrice = this.parseNumberQueryParam(queryParams.get('minPrice'));
    const maxPrice = this.parseNumberQueryParam(queryParams.get('maxPrice'));
    const searchTerm = queryParams.get('q')?.trim() ?? '';

    return {
      brands,
      ratings,
      freeDelivery,
      newItems,
      minPrice,
      maxPrice,
      searchTerm,
    };
  }

  private parseStringListQueryParam(value: string | null): string[] {
    if (!value) return [];
    return value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => !!v);
  }

  private parseNumberListQueryParam(value: string | null): number[] {
    if (!value) return [];
    return value
      .split(',')
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));
  }

  private parseNumberQueryParam(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private buildFilterQueryParams() {
    const filters = this.currentFilters();
    return {
      sort: this.sortBy() || 'latest',
      q: this.searchTerm() || null,
      brands: filters.brands.length ? filters.brands.join(',') : null,
      ratings: filters.ratings.length ? filters.ratings.join(',') : null,
      freeDelivery: filters.freeDelivery ? '1' : null,
      newItems: filters.newItems ? '1' : null,
      minPrice: filters.minPrice || filters.minPrice === 0 ? filters.minPrice : null,
      maxPrice: filters.maxPrice || filters.maxPrice === 0 ? filters.maxPrice : null,
    };
  }

  private async syncQueryParams() {
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.buildFilterQueryParams(),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private async ensureDefaultQueryParams(queryParams: ParamMap) {
    if (queryParams.has('sort')) return;

    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sort: 'latest' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private async reloadCurrentCategoryForLanguage(_lang: 'en' | 'ar') {
    const slug = this.route.snapshot.paramMap.get('slug');
    if (!slug) {
      return;
    }

    const subSlug = this.route.snapshot.paramMap.get('sub');
    const sub2Slug = this.route.snapshot.paramMap.get('sub2');
    const queryParams = this.route.snapshot.queryParamMap;

    await this.loadInitialData(slug);
    this.applySubCategoryFilterFromRoute(subSlug, sub2Slug);

    if (this.hasActiveNonSubCategoryFilters() || !!subSlug) {
      await this.applyFiltersAndSearch(true);
    }
  }
}
